// JPL approximate planetary positions.
// Implementation of the Standish (1992) algorithm for "Keplerian elements
// for approximate positions of the major planets," published by JPL Solar
// System Dynamics. Valid for the range 1800-2050 with errors of a few
// arcminutes for the outer planets and sub-arcminute for the inner.
//
// Source: https://ssd.jpl.nasa.gov/planets/approx_pos.html
//
// Each planet has a J2000 element vector (a, e, i, L, longPeri, longNode)
// and a per-century rate vector. Position is computed by linearly
// extrapolating the elements to the requested epoch, solving Kepler's
// equation for the eccentric anomaly, and rotating from the orbital plane
// into heliocentric ecliptic coordinates.
//
// Output is heliocentric ecliptic in astronomical units. The ecliptic
// frame uses x toward the vernal equinox, y 90° east in the ecliptic
// plane, and z toward the ecliptic north pole (right-handed). Three.js
// scenes are typically y-up, so the consumer is expected to remap axes.

const DEG = Math.PI / 180;
const J2000_JD = 2451545.0;

// Each entry: { el: [a_au, e, i_deg, L_deg, longPeri_deg, longNode_deg],
//               rate: [a/Cy, e/Cy, i_deg/Cy, L_deg/Cy, longPeri/Cy, longNode/Cy] }
// "longPeri" = longitude of perihelion = longNode + argPeri.
const PLANETS = {
  mercury: {
    el:   [0.38709927, 0.20563593,  7.00497902, 252.25032350,  77.45779628,  48.33076593],
    rate: [0.00000037, 0.00001906, -0.00594749, 149472.67411175, 0.16047689, -0.12534081],
  },
  venus: {
    el:   [0.72333566, 0.00677672,  3.39467605, 181.97909950, 131.60246718,  76.67984255],
    rate: [0.00000390, -0.00004107, -0.00078890, 58517.81538729, 0.00268329, -0.27769418],
  },
  earth: {
    el:   [1.00000261, 0.01671123, -0.00001531, 100.46457166, 102.93768193,   0.00000000],
    rate: [0.00000562, -0.00004392, -0.01294668, 35999.37244981, 0.32327364,   0.00000000],
  },
  mars: {
    el:   [1.52371034, 0.09339410,  1.84969142,  -4.55343205, -23.94362959,  49.55953891],
    rate: [0.00001847, 0.00007882, -0.00813131, 19140.30268499, 0.44441088, -0.29257343],
  },
  jupiter: {
    el:   [5.20288700, 0.04838624,  1.30439695,  34.39644051,  14.72847983, 100.47390909],
    rate: [-0.00011607, -0.00013253, -0.00183714, 3034.74612775, 0.21252668,  0.20469106],
  },
  saturn: {
    el:   [9.53667594, 0.05386179,  2.48599187,  49.95424423,  92.59887831, 113.66242448],
    rate: [-0.00125060, -0.00050991, 0.00193609, 1222.49362201, -0.41897216, -0.28867794],
  },
  uranus: {
    el:   [19.18916464, 0.04725744, 0.77263783, 313.23810451, 170.95427630,  74.01692503],
    rate: [-0.00196176, -0.00004397, -0.00242939, 428.48202785, 0.40805281,   0.04240589],
  },
  neptune: {
    el:   [30.06992276, 0.00859048, 1.77004347, -55.12002969,  44.96476227, 131.78422574],
    rate: [0.00026291, 0.00005105, 0.00035372, 218.45945325, -0.32241464, -0.00508664],
  },
  // Pluto extended set (less accurate than JPL's published range — it's
  // not actually in the standard table — but the orbit is well enough
  // characterised that linear elements approximate it for the same era).
  pluto: {
    el:   [39.48211675, 0.24882730, 17.14001206, 238.92903833, 224.06891629, 110.30393684],
    rate: [-0.00031596, 0.00005170,  0.00004818, 145.20780515, -0.04062942, -0.01183482],
  },
};

// Reduce an angle in degrees to [-180, 180].
function reduceDeg(deg) {
  let x = deg % 360;
  if (x > 180) x -= 360;
  if (x < -180) x += 360;
  return x;
}

// Newton iteration for Kepler's equation M = E - e*sin(E). Returns E (rad).
// Exported so the fallback (non-ephemeris) orbits in main.js can share
// the same solver instead of advancing true anomaly at a constant rate.
export function solveKepler(M, e) {
  let E = M + e * Math.sin(M);
  for (let i = 0; i < 8; i++) {
    const dE = (E - e * Math.sin(E) - M) / (1 - e * Math.cos(E));
    E -= dE;
    if (Math.abs(dE) < 1e-9) break;
  }
  return E;
}

// Julian Date from a JS Date (or millisecond epoch). UTC, no leap-second
// correction — for visual ephemeris work the difference is negligible.
export function dateToJD(date = new Date()) {
  const ms = typeof date === 'number' ? date : date.getTime();
  return ms / 86400000 + 2440587.5;
}

// Solved orbital state for a planet at a given Julian date. Returns the
// elements that have been linearly extrapolated to the epoch plus the
// solved eccentric and true anomalies. Useful for callers that want the
// position in different frames without redoing Kepler each time.
export function getOrbitState(name, jd) {
  const planet = PLANETS[name];
  if (!planet) return null;
  const T = (jd - J2000_JD) / 36525.0;
  const a       = planet.el[0] + planet.rate[0] * T;
  const e       = planet.el[1] + planet.rate[1] * T;
  const i       = (planet.el[2] + planet.rate[2] * T) * DEG;
  const L       = planet.el[3] + planet.rate[3] * T;
  const lpDeg   = planet.el[4] + planet.rate[4] * T;
  const lnDeg   = planet.el[5] + planet.rate[5] * T;

  // Mean anomaly, reduced to [-180, 180] deg.
  const M = reduceDeg(L - lpDeg) * DEG;
  const E = solveKepler(M, e);

  // True anomaly from eccentric anomaly.
  const sinE = Math.sin(E);
  const cosE = Math.cos(E);
  const trueAnomaly = Math.atan2(Math.sqrt(1 - e * e) * sinE, cosE - e);
  // Heliocentric distance.
  const r = a * (1 - e * cosE);

  return {
    a, e, i,
    longPeri: lpDeg * DEG,
    longNode: lnDeg * DEG,
    argPeri: (lpDeg - lnDeg) * DEG,
    meanAnomaly: M,
    eccentricAnomaly: E,
    trueAnomaly,
    r,
  };
}

// Heliocentric ecliptic position in AU at a given Julian date.
// Returns { x, y, z } where the ecliptic plane is xy and z is north.
export function getPlanetPosition(name, jd) {
  const s = getOrbitState(name, jd);
  if (!s) return null;

  // Position in the orbital plane. Perihelion at +x.
  const cosE = Math.cos(s.eccentricAnomaly);
  const sinE = Math.sin(s.eccentricAnomaly);
  const xp = s.a * (cosE - s.e);
  const yp = s.a * Math.sqrt(1 - s.e * s.e) * sinE;

  // Standard rotation orbital → ecliptic: Rz(Ω) · Rx(i) · Rz(ω).
  const cosO = Math.cos(s.longNode);
  const sinO = Math.sin(s.longNode);
  const cosI = Math.cos(s.i);
  const sinI = Math.sin(s.i);
  const cosW = Math.cos(s.argPeri);
  const sinW = Math.sin(s.argPeri);

  const x =
    xp * (cosW * cosO - sinW * sinO * cosI) +
    yp * (-sinW * cosO - cosW * sinO * cosI);
  const y =
    xp * (cosW * sinO + sinW * cosO * cosI) +
    yp * (-sinW * sinO + cosW * cosO * cosI);
  const z = xp * (sinW * sinI) + yp * (cosW * sinI);

  return { x, y, z };
}

// A small body's heliocentric ecliptic J2000 position in AU at jd from its
// osculating elements: a (AU), e, and i, node, peri and M (degrees) at
// epochJD, with n its mean motion in degrees a day, or the Sun's own from a
// where it has none. Fills and returns out ([x, y, z]).
export function elementsPosition(el, jd, epochJD, out = [0, 0, 0]) {
  // Gauss's constant, 0.01720209895 radians a day, in degrees.
  const n = el.n != null ? el.n : 0.9856076686 / Math.pow(el.a, 1.5);
  const M = reduceDeg(el.M + n * (jd - epochJD)) * DEG;
  const E = solveKepler(M, el.e);
  const xp = el.a * (Math.cos(E) - el.e);
  const yp = el.a * Math.sqrt(1 - el.e * el.e) * Math.sin(E);
  const cosO = Math.cos(el.node * DEG);
  const sinO = Math.sin(el.node * DEG);
  const cosI = Math.cos(el.i * DEG);
  const sinI = Math.sin(el.i * DEG);
  const cosW = Math.cos(el.peri * DEG);
  const sinW = Math.sin(el.peri * DEG);
  out[0] = xp * (cosW * cosO - sinW * sinO * cosI) + yp * (-sinW * cosO - cosW * sinO * cosI);
  out[1] = xp * (cosW * sinO + sinW * cosO * cosI) + yp * (-sinW * sinO + cosW * cosO * cosI);
  out[2] = xp * (sinW * sinI) + yp * (cosW * sinI);
  return out;
}

// True anomaly only (orbit phase). Useful for artistic mode where we
// keep the compressed scene distance but want the right phase position.
export function getTrueAnomaly(name, jd) {
  const s = getOrbitState(name, jd);
  return s ? s.trueAnomaly : null;
}

// All planet names this module supports.
export const SUPPORTED_PLANETS = Object.keys(PLANETS);
